import { CLEAR_LENS, DEFAULT_APPEARANCE, formatIdr, LENS_TINTS, SIGNATURE_LENS_IDS, SIGNATURE_SUNSET_SUPPLEMENT_IDR, SIGNATURE_TINT_DENSITY, SPECIAL_LENSES, type AppearanceChoice } from '../config/appearance';
import { acetateGroups, chipLabel, chipNeighbour, chipSpriteStyle, peekGap } from './acetateChips';
import { btn, h, icon } from './icons';
import { words } from './words';

/** Mazzucchelli tortoise chip photographed in the workshop (design handoff asset): « Original » fallback. */
const TORTOISE_CHIP = new URL('./assets/acetate-ecaille.jpg', import.meta.url).href;
/**
 * A110V: one sample chip per acetate of the library, in one sprite (acetates/make_chips.py).
 * Only fetched once a chip is rendered (the Colours view is `hidden` until opened).
 */
const CHIP_SPRITE = new URL('./assets/acetate-chips.jpg', import.meta.url).href;
const GLOSS = 'linear-gradient(160deg,rgba(255,255,255,.26),rgba(255,255,255,0) 34%)';
/** Matte lens discs: flat tint, no reflection. */
const LENS_DISCS: Record<string, string> = {
  brown: 'linear-gradient(180deg,rgba(86,52,32,.92),rgba(140,98,68,.8)),#f1ece6',
  green: 'linear-gradient(180deg,rgba(31,64,52,.92),rgba(70,110,92,.8)),#eef1ee',
  grey: 'linear-gradient(180deg,rgba(50,53,55,.92),rgba(102,106,108,.8)),#eeeeee',
  blue: 'linear-gradient(180deg,rgba(34,68,120,.92),rgba(82,122,178,.8)),#edf0f5',
  /** A200 « Lunettes de vue »: a clear lens (a light sheen, drawn with a hairline ring in CSS). */
  clear: 'linear-gradient(160deg,rgba(255,255,255,.98),rgba(226,233,232,.9) 60%,rgba(244,247,246,.95)),#eef2f1',
};
/** Sunset swatches (top, bottom), shared with « Voir le paysage ». */
export const SUNSET_DISCS: Record<string, readonly [string, string]> = {
  sunset: ['#5a3fa0', '#f07a34'], sunrise: ['#7fb3df', '#f2d23c'], dawn: ['#6a6f9e', '#f08a6c'], stellar: ['#2d5be0', '#e02a7c'],
  aurora: ['#9a5ae6', '#8fb4f2'], bloom: ['#b87aa8', '#f06a9a'], magma: ['#e8332a', '#ff6a3a'], coral: ['#9a2d5a', '#e48ab8'],
  sun: ['#f6cf2a', '#f2b818'], sky: ['#2f6be6', '#4a90f0'], lagoon: ['#7fc6d6', '#2a6a7e'],
};
const INTENSITIES = [30, 85] as const;
type ColourTab = 'ace' | 'lens' | 'sun';
/** Last colour tab, kept for this page only (a new model reopens on the same tab). */
let lastTab: ColourTab = 'ace';

function rgba(hex: string | undefined, alpha: number): string | null {
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return null;
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Display name of an acetate preset, for the title chip. */
export function acetateName(id: string): string | null {
  return id === 'original' ? null : words().acetates[id] ?? null;
}

export interface OriginalLook { acetateColor?: string; lensColor?: string }

export interface AppearanceOptions {
  /** A110T: « Voir le paysage » (Sunset tab). Only given on phones and tablets. */
  onLookThrough?: () => void;
  /** A110Z: « Gravure », 4th tab of the phone view — opens the full-screen engraving view at once. */
  onEngrave?: () => void;
  /**
   * A200 Signature: the lenses only — « Original », the four classic tints and « Lunettes de vue »
   * (David, 8 October: no gradient, no acetate). Same discs, same DA. A202 (David, 11 October): plus the eleven
   * Sunset lenses, a supplement of Rp 990.000, in their own tab like the Handmade.
   */
  lensOnly?: boolean;
}

/**
 * A42E « Couleurs » view. Mobile: three tabs (Acétate | Verres | Sunset) with a
 * one-line footer. Desktop: every section stacked in the right column.
 * Live session choices only: no variant, order or stored personal data.
 */
export class AppearanceControls {
  readonly root = h('div', 'mt-view mt-colour-view');
  private choice: AppearanceChoice = { ...DEFAULT_APPEARANCE };
  /** A110T: applies a lens chosen outside this view (« Voir le paysage »). */
  private applyLens: (lens: string, density: number) => void = () => undefined;

  constructor(onChange: (choice: AppearanceChoice) => void, laminated = false, initial: AppearanceChoice = DEFAULT_APPEARANCE, original: OriginalLook = {}, options: AppearanceOptions = {}) {
    this.choice = { ...initial };
    let standardGradient = initial.gradient;
    const w = words();
    this.root.dataset.view = 'colours';
    this.root.setAttribute('role', 'group');
    this.root.setAttribute('aria-label', w.pill[1]);
    const isSunset = () => SPECIAL_LENSES.some(s => s.id === this.choice.lens);
    // A201: « Lunettes de vue » on Handmade — clear lenses, so no gradient and no intensity.
    const isClear = () => this.choice.lens === CLEAR_LENS.id;
    const noGradient = () => isSunset() || isClear();
    if (isSunset()) lastTab = 'sun';
    const refreshers: Array<() => void> = [];
    const emit = () => { refreshers.forEach(r => r()); onChange({ ...this.choice }); };
    if (options.lensOnly) { this.buildLensOnly(initial, original, emit, refreshers, options); return; }

    // Mobile tabs
    const tabs = h('div', 'mt-seg mt-ctabs'); tabs.setAttribute('role', 'tablist');
    const tabIds: ColourTab[] = ['ace', 'lens', 'sun'];
    const tabButtons = tabIds.map((id, i) => {
      const b = btn('mt-seg-item mt-ctab', id === 'sun' ? `${w.tabs[i]} · ${SPECIAL_LENSES.length}` : w.tabs[i]);
      b.setAttribute('role', 'tab'); b.dataset.tab = id;
      b.onclick = () => setTab(id);
      tabs.append(b); return b;
    });
    const setTab = (id: ColourTab) => {
      lastTab = id; this.root.dataset.ctab = id;
      tabButtons.forEach(b => { const on = b.dataset.tab === id; b.setAttribute('aria-selected', String(on)); b.tabIndex = on ? 0 : -1; });
    };
    // A110Z: the 4th tab is a door, not a tab: it opens the Gravure view and leaves the colour tabs as they are.
    if (options.onEngrave) {
      const g = btn('mt-seg-item mt-ctab mt-ctab--engrave', w.engrave.tab);
      g.dataset.tab = 'engrave';
      g.setAttribute('aria-selected', 'false');
      g.tabIndex = -1;
      g.onclick = () => options.onEngrave?.();
      tabs.append(g);
      tabs.dataset.count = '4';
    }

    // A110V acetate chips: « Original » + the 36 plates of the library.
    // Mobile: one horizontal strip (snap, edge fades, half a chip peeking); desktop: a grid by family.
    const ace = h('section', 'mt-csec mt-csec--ace');
    const aceLabel = laminated ? w.mainAcetate : w.sections[0];
    const aceTitle = h('h3', 'mt-csec-title', aceLabel);
    const chips = h('div', 'mt-chips'); chips.setAttribute('role', 'group'); chips.setAttribute('aria-label', aceLabel);
    chips.toggleAttribute('data-at-start', true);
    const originalChip = rgba(original.acetateColor, 1)
      ? `${GLOSS},linear-gradient(160deg,${rgba(original.acetateColor, .82)},${rgba(original.acetateColor, 1)} 60%)`
      : `${GLOSS},linear-gradient(rgba(226,170,96,.22),rgba(226,170,96,.22)),url("${TORTOISE_CHIP}") center/cover`;
    const chipButtons: HTMLButtonElement[] = [];
    const addChip = (id: string, name: string, paint: (card: HTMLElement) => void) => {
      const b = btn('mt-acetate-chip');
      b.dataset.id = id; b.title = name; b.tabIndex = -1;
      b.setAttribute('aria-label', chipLabel(aceLabel, name));
      const card = h('span', 'mt-chip-card'); card.setAttribute('aria-hidden', 'true'); paint(card);
      b.append(card);
      b.onclick = () => { this.choice.acetate = id; emit(); };
      chips.append(b); chipButtons.push(b);
    };
    acetateGroups().forEach((group, i) => {
      // Family subheading (desktop grid only); « Original » opens the first family.
      chips.append(h('span', 'mt-chips-family', w.acetateFamilies[group.family]));
      if (i === 0) addChip('original', w.original, card => { card.style.background = originalChip; });
      for (const a of group.acetates) {
        addChip(a.id, w.acetates[a.id] ?? a.id, card => {
          const cell = chipSpriteStyle(a.id);
          card.style.backgroundColor = a.color; // while the sprite loads, or for a plate not in it yet
          card.style.backgroundImage = cell ? `url("${CHIP_SPRITE}")` : GLOSS;
          if (cell) { card.style.backgroundSize = cell.size; card.style.backgroundPosition = cell.position; }
        });
      }
    });
    const selectedChip = () => chipButtons.find(b => b.dataset.id === this.choice.acetate) ?? chipButtons[0]!;
    const setRoving = (target: HTMLButtonElement) => chipButtons.forEach(b => { b.tabIndex = b === target ? 0 : -1; });
    const motion = (): ScrollBehavior => (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');
    /** Brings a chip into view inside the strip (and the panel / column around it), never the page. */
    const reveal = (chip: HTMLButtonElement, behavior: ScrollBehavior) => {
      if (!chips.isConnected || chips.clientWidth === 0) return;
      const max = chips.scrollWidth - chips.clientWidth;
      if (max > 1) {
        const cs = getComputedStyle(chips);
        const padL = parseFloat(cs.scrollPaddingLeft) || 0;
        const padR = parseFloat(cs.scrollPaddingRight) || 0;
        const left = chip.offsetLeft, right = left + chip.offsetWidth;
        let target = chips.scrollLeft;
        if (left - padL < chips.scrollLeft) target = left - padL;
        else if (right + padR > chips.scrollLeft + chips.clientWidth) {
          // The first snap position (a chip at the start edge) that shows the whole chip.
          const need = right + padR - chips.clientWidth;
          const snaps = chipButtons.map(b => b.offsetLeft - padL).filter(x => x >= need - 0.5);
          target = snaps.length ? Math.min(...snaps) : need;
        }
        target = Math.max(0, Math.min(max, target));
        if (Math.abs(target - chips.scrollLeft) > 0.5) chips.scrollTo({ left: target, behavior });
      }
      // The desktop grid scrolls itself; on phones the panel / column around it may.
      for (let box: HTMLElement | null = chips; box && box !== document.body && !box.classList.contains('mt-root'); box = box.parentElement) {
        const oy = getComputedStyle(box).overflowY;
        if ((oy !== 'auto' && oy !== 'scroll') || box.scrollHeight <= box.clientHeight + 1) continue;
        const r = chip.getBoundingClientRect(), b = box.getBoundingClientRect();
        const dy = r.top - 4 < b.top ? r.top - 4 - b.top : r.bottom + 4 > b.bottom ? r.bottom + 4 - b.bottom : 0;
        if (dy) box.scrollBy({ top: dy, behavior });
        break;
      }
    };
    const updateEdges = () => {
      const max = chips.scrollWidth - chips.clientWidth;
      chips.toggleAttribute('data-at-start', chips.scrollLeft <= 1);
      chips.toggleAttribute('data-at-end', chips.scrollLeft >= max - 1);
    };
    let edgeFrame = 0;
    chips.addEventListener('scroll', () => {
      if (!edgeFrame) edgeFrame = requestAnimationFrame(() => { edgeFrame = 0; updateEdges(); });
    }, { passive: true });
    // Shown (tab opened, view opened) or resized: fit the strip spacing, then show the selected chip.
    let shownWidth = 0;
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(() => {
        const width = chips.clientWidth;
        if (width > 0 && width !== shownWidth) {
          if (getComputedStyle(chips).display === 'flex') chips.style.setProperty('--mt-chip-gap', `${peekGap(width)}px`);
          else chips.style.removeProperty('--mt-chip-gap');
          reveal(selectedChip(), 'auto');
          updateEdges();
        }
        shownWidth = width;
      }).observe(chips);
    }
    chips.addEventListener('focusin', e => {
      const b = (e.target as Element).closest<HTMLButtonElement>('.mt-acetate-chip');
      if (b) setRoving(b);
    });
    chips.addEventListener('keydown', e => {
      const i = chipButtons.indexOf(document.activeElement as HTMLButtonElement);
      if (i < 0 || e.altKey || e.ctrlKey || e.metaKey) return;
      const rects = chipButtons.map(b => { const r = b.getBoundingClientRect(); return { x: r.left, y: r.top, w: r.width, h: r.height }; });
      const j = chipNeighbour(e.key, i, rects);
      if (j === null) return;
      e.preventDefault();
      const target = chipButtons[j]!;
      setRoving(target);
      target.focus({ preventScroll: true });
      reveal(target, 'auto');
    });
    let shownAcetate = this.choice.acetate;
    refreshers.push(() => {
      const id = this.choice.acetate;
      const selected = selectedChip();
      aceTitle.textContent = chipLabel(aceLabel, id === 'original' ? w.original : w.acetates[id] ?? id);
      chipButtons.forEach(b => b.setAttribute('aria-pressed', String(b.dataset.id === id)));
      if (!chips.contains(document.activeElement)) setRoving(selected);
      if (id !== shownAcetate) { shownAcetate = id; reveal(selected, motion()); }
    });
    ace.append(aceTitle, chips);

    // Lens discs (classic + Sunset)
    const disc = (id: string, name: string, background: string, group: HTMLElement) => {
      const b = btn('mt-lens-disc');
      b.dataset.id = id;
      const swatch = h('span', id === CLEAR_LENS.id ? 'mt-disc mt-disc--clear' : 'mt-disc'); swatch.style.background = background; swatch.setAttribute('aria-hidden', 'true');
      const label = h('span', 'mt-disc-label', name);
      b.append(swatch, label);
      b.onclick = () => {
        this.choice.lens = id;
        if (id === 'original') { this.choice.density = initial.density; standardGradient = initial.gradient; }
        else if (id === CLEAR_LENS.id) this.choice.density = initial.density;
        else if (!INTENSITIES.includes(this.choice.density as typeof INTENSITIES[number])) this.choice.density = 85;
        this.choice.gradient = noGradient() ? false : standardGradient;
        emit();
      };
      group.append(b); return b;
    };
    const lensSec = h('section', 'mt-csec mt-csec--lens');
    const lensHead = h('div', 'mt-csec-head');
    const lensTitle = h('h3', 'mt-csec-title', w.sections[1]);
    // A201: six discs with « Lunettes de vue », laid out like the Signature's (one row, labels wrap).
    const lensDiscs = h('div', 'mt-discs mt-discs--six'); lensDiscs.setAttribute('role', 'group'); lensDiscs.setAttribute('aria-label', w.sections[1]);
    const originalLens = rgba(original.lensColor, .94)
      ? `linear-gradient(180deg,${rgba(original.lensColor, .94)},${rgba(original.lensColor, .7)}),#f3eee8`
      : 'linear-gradient(180deg,rgba(94,56,38,.94),rgba(196,147,115,.7)),#f3eee8';
    const lensButtons = [
      disc('original', w.original, originalLens, lensDiscs),
      ...LENS_TINTS.map(l => disc(l.id, w.lenses[l.id] ?? l.id, LENS_DISCS[l.id] ?? l.color, lensDiscs)),
      disc(CLEAR_LENS.id, w.lenses[CLEAR_LENS.id] ?? CLEAR_LENS.id, LENS_DISCS[CLEAR_LENS.id] ?? CLEAR_LENS.color, lensDiscs),
    ];
    const sunSec = h('section', 'mt-csec mt-csec--sun');
    const sunTitle = h('h3', 'mt-csec-title', `${w.sections[2]} · ${SPECIAL_LENSES.length}`);
    const sunDiscs = h('div', 'mt-discs mt-discs--sun'); sunDiscs.setAttribute('role', 'group'); sunDiscs.setAttribute('aria-label', sunTitle.textContent!);
    const sunButtons = SPECIAL_LENSES.map(s => { const [top, bottom] = SUNSET_DISCS[s.id] ?? [s.top, s.bottom]; return disc(s.id, s.label, `linear-gradient(180deg,${top},${bottom})`, sunDiscs); });
    refreshers.push(() => [...lensButtons, ...sunButtons].forEach(b => b.setAttribute('aria-pressed', String(b.dataset.id === this.choice.lens))));

    // Gradient switch (classic lenses only) — one in the desktop title row, one in the mobile footer.
    const makeSwitch = () => {
      const s = btn('mt-switch');
      s.setAttribute('role', 'switch');
      const track = h('span', 'mt-switch-track'); track.append(h('span', 'mt-switch-knob'));
      s.append(track, document.createTextNode(w.gradient));
      s.onclick = () => {
        if (noGradient()) return;
        standardGradient = !this.choice.gradient; this.choice.gradient = standardGradient; emit();
      };
      refreshers.push(() => {
        s.setAttribute('aria-checked', String(!noGradient() && this.choice.gradient));
        s.setAttribute('aria-disabled', String(noGradient()));
        s.classList.toggle('mt-switch--disabled', noGradient());
      });
      return s;
    };
    lensHead.append(lensTitle, makeSwitch());
    lensSec.append(lensHead, lensDiscs);
    sunSec.append(sunTitle, sunDiscs);

    // Intensity: Light 30 % / Dark 85 % (no percentage shown). Tapping the active one again restores the original.
    const makeIntensity = (className: string, group: HTMLElement) => INTENSITIES.map((value, i) => {
      const b = btn(className, w.intensity[i]);
      b.dataset.density = String(value);
      b.onclick = () => { if (isClear()) return; this.choice.density = this.choice.density === value ? initial.density : value; emit(); };
      refreshers.push(() => {
        b.setAttribute('aria-pressed', String(!isClear() && this.choice.density === value));
        b.setAttribute('aria-disabled', String(isClear()));
        b.classList.toggle('mt-int--off', isClear());
      });
      group.append(b); return b;
    });
    const intSec = h('section', 'mt-csec mt-csec--int');
    const intRow = h('div', 'mt-int-row'); intRow.setAttribute('role', 'group'); intRow.setAttribute('aria-label', w.sections[3]);
    makeIntensity('mt-int-btn', intRow);
    intSec.append(h('h3', 'mt-csec-title', w.sections[3]), intRow);

    const makeReset = (withLabel: boolean) => {
      const b = btn('mt-reset');
      b.setAttribute('aria-label', w.originalColours);
      b.append(icon('reset'));
      if (withLabel) b.append(h('span', 'mt-reset-label', w.original));
      b.onclick = () => { this.choice = { ...initial }; standardGradient = initial.gradient; emit(); };
      return b;
    };

    // A110T « Voir le paysage » : the rear camera through the Sunset lens (phones and tablets only).
    // From another lens, it starts on « Sunset ». Mobile: in the Sunset footer; desktop layout: in the Sunset title row.
    const makeLook = () => {
      const b = btn('mt-look');
      b.setAttribute('aria-label', w.lookThrough);
      b.append(icon('landscape'), h('span', 'mt-look-label mt-long', w.lookThrough), h('span', 'mt-look-label mt-short', w.lookThroughShort));
      b.onclick = () => {
        if (!isSunset()) this.applyLens('sunset', this.choice.density);
        options.onLookThrough?.();
      };
      return b;
    };
    this.applyLens = (lens, density) => {
      if (!SPECIAL_LENSES.some(s => s.id === lens)) return;
      this.choice.lens = lens;
      this.choice.density = INTENSITIES.includes(density as typeof INTENSITIES[number]) ? density : 85;
      this.choice.gradient = false;
      emit();
    };
    if (options.onLookThrough) {
      this.root.classList.add('mt-colour-view--look');
      const sunHead = h('div', 'mt-csec-head');
      sunHead.append(sunTitle, makeLook());
      sunSec.prepend(sunHead);
    }

    // Mobile footer: note (Acétate) or switch + intensity (Verres / Sunset), then « Voir le paysage » (Sunset) and reset.
    const foot = h('div', 'mt-cfoot');
    const note = h('span', 'mt-note mt-only-ace', w.materialsNote);
    const lensTools = h('div', 'mt-lens-tools mt-only-lens');
    const footSwitch = makeSwitch(); footSwitch.classList.add('mt-only-classic');
    const chipsRow = h('div', 'mt-int-chips'); chipsRow.setAttribute('role', 'group'); chipsRow.setAttribute('aria-label', w.sections[3]);
    makeIntensity('mt-int-chip', chipsRow);
    lensTools.append(footSwitch, chipsRow);
    foot.append(note, lensTools);
    if (options.onLookThrough) { const look = makeLook(); look.classList.add('mt-only-sun'); foot.append(look); }
    foot.append(makeReset(true));

    // Desktop footer
    const deskFoot = h('div', 'mt-cfoot-desk');
    deskFoot.append(h('span', 'mt-note', w.materialsNote), makeReset(true));

    this.root.append(tabs, ace, lensSec, sunSec, intSec, foot, deskFoot);
    setTab(lastTab);
    refreshers.forEach(r => r());
  }

  /**
   * A200 Signature: one lens section (Original + classic tints + « Lunettes de vue ») and the reset.
   * A202 (David, 11 octobre : « les verres Sunset pour les Signature, c'est une addition de 990k ») : a second tab,
   * « Sunset · 11 », with the supplement written under the discs, Clair / Foncé and « Voir le paysage » (phones).
   */
  private buildLensOnly(initial: AppearanceChoice, original: OriginalLook, emit: () => void, refreshers: Array<() => void>, options: AppearanceOptions): void {
    const w = words();
    this.root.classList.add('mt-colour-view--lens-only');
    const isSunset = () => SPECIAL_LENSES.some(s => s.id === this.choice.lens);
    type SigTab = 'lens' | 'sun';
    const tabs = h('div', 'mt-seg mt-ctabs'); tabs.setAttribute('role', 'tablist');
    const tabButtons = (['lens', 'sun'] as const).map(id => {
      const b = btn('mt-seg-item mt-ctab', id === 'sun' ? `${w.tabs[2]} · ${SPECIAL_LENSES.length}` : w.tabs[1]);
      b.setAttribute('role', 'tab'); b.dataset.tab = id;
      b.onclick = () => setTab(id);
      tabs.append(b); return b;
    });
    const setTab = (id: SigTab) => {
      this.root.dataset.ctab = id;
      tabButtons.forEach(b => { const on = b.dataset.tab === id; b.setAttribute('aria-selected', String(on)); b.tabIndex = on ? 0 : -1; });
    };

    const lensSec = h('section', 'mt-csec mt-csec--lens');
    const lensTitle = h('h3', 'mt-csec-title', w.sections[1]);
    const discs = h('div', 'mt-discs mt-discs--signature'); discs.setAttribute('role', 'group'); discs.setAttribute('aria-label', w.sections[1]);
    const originalLens = rgba(original.lensColor, .94)
      ? `linear-gradient(180deg,${rgba(original.lensColor, .94)},${rgba(original.lensColor, .7)}),#f3eee8`
      : 'linear-gradient(180deg,rgba(94,56,38,.94),rgba(196,147,115,.7)),#f3eee8';
    const ids = ['original', ...SIGNATURE_LENS_IDS] as const;
    const buttons = ids.map(id => {
      const b = btn('mt-lens-disc');
      b.dataset.id = id;
      const swatch = h('span', id === CLEAR_LENS.id ? 'mt-disc mt-disc--clear' : 'mt-disc');
      swatch.style.background = id === 'original' ? originalLens : LENS_DISCS[id] ?? LENS_TINTS.find(l => l.id === id)?.color ?? CLEAR_LENS.color;
      swatch.setAttribute('aria-hidden', 'true');
      const name = id === 'original' ? w.original : w.lenses[id] ?? id;
      b.append(swatch, h('span', 'mt-disc-label', name));
      b.onclick = () => {
        this.choice.acetate = 'original';
        this.choice.lens = id;
        this.choice.density = id === 'original' || id === CLEAR_LENS.id ? initial.density : SIGNATURE_TINT_DENSITY;
        this.choice.gradient = id === 'original' ? initial.gradient : false;
        emit();
      };
      discs.append(b);
      return b;
    });
    lensSec.append(lensTitle, discs);

    // Sunset: the eleven lenses, the supplement, Clair / Foncé (Foncé first chosen, as on the Handmade).
    const price = w.sunsetSupplement(formatIdr(SIGNATURE_SUNSET_SUPPLEMENT_IDR));
    const sunSec = h('section', 'mt-csec mt-csec--sun');
    const sunHead = h('div', 'mt-csec-head');
    const sunTitle = h('h3', 'mt-csec-title', `${w.sections[2]} · ${SPECIAL_LENSES.length}`);
    const sunDiscs = h('div', 'mt-discs mt-discs--sun'); sunDiscs.setAttribute('role', 'group'); sunDiscs.setAttribute('aria-label', sunTitle.textContent!);
    const sunButtons = SPECIAL_LENSES.map(sp => {
      const [top, bottom] = SUNSET_DISCS[sp.id] ?? [sp.top, sp.bottom];
      const b = btn('mt-lens-disc');
      b.dataset.id = sp.id;
      const swatch = h('span', 'mt-disc'); swatch.style.background = `linear-gradient(180deg,${top},${bottom})`; swatch.setAttribute('aria-hidden', 'true');
      b.append(swatch, h('span', 'mt-disc-label', sp.label));
      b.onclick = () => {
        this.choice.acetate = 'original';
        this.choice.lens = sp.id;
        if (!INTENSITIES.includes(this.choice.density as typeof INTENSITIES[number])) this.choice.density = 85;
        this.choice.gradient = false;
        emit();
      };
      sunDiscs.append(b);
      return b;
    });
    const priceNote = h('p', 'mt-note mt-sun-price', price);
    sunHead.append(sunTitle);
    sunSec.append(sunHead, sunDiscs, priceNote);
    refreshers.push(() => [...buttons, ...sunButtons].forEach(b => b.setAttribute('aria-pressed', String(b.dataset.id === this.choice.lens))));

    // Clair / Foncé: Sunset lenses only (the classic tints keep the Signature density).
    const makeIntensity = (className: string, group: HTMLElement) => INTENSITIES.map((value, i) => {
      const b = btn(className, w.intensity[i]);
      b.dataset.density = String(value);
      b.onclick = () => { if (!isSunset()) return; this.choice.density = value; emit(); };
      refreshers.push(() => {
        b.setAttribute('aria-pressed', String(isSunset() && this.choice.density === value));
        b.setAttribute('aria-disabled', String(!isSunset()));
        b.classList.toggle('mt-int--off', !isSunset());
      });
      group.append(b); return b;
    });
    const intSec = h('section', 'mt-csec mt-csec--int');
    const intRow = h('div', 'mt-int-row'); intRow.setAttribute('role', 'group'); intRow.setAttribute('aria-label', w.sections[3]);
    makeIntensity('mt-int-btn', intRow);
    intSec.append(h('h3', 'mt-csec-title', w.sections[3]), intRow);

    const makeReset = () => {
      const b = btn('mt-reset');
      b.setAttribute('aria-label', w.originalColours);
      b.append(icon('reset'), h('span', 'mt-reset-label', w.original));
      b.onclick = () => { this.choice = { ...initial }; emit(); };
      return b;
    };
    const makeLook = () => {
      const b = btn('mt-look');
      b.setAttribute('aria-label', w.lookThrough);
      b.append(icon('landscape'), h('span', 'mt-look-label mt-long', w.lookThrough), h('span', 'mt-look-label mt-short', w.lookThroughShort));
      b.onclick = () => {
        if (!isSunset()) this.applyLens('sunset', this.choice.density);
        options.onLookThrough?.();
      };
      return b;
    };
    this.applyLens = (lens, density) => {
      if (!SPECIAL_LENSES.some(sp => sp.id === lens)) return;
      this.choice.acetate = 'original';
      this.choice.lens = lens;
      this.choice.density = INTENSITIES.includes(density as typeof INTENSITIES[number]) ? density : 85;
      this.choice.gradient = false;
      emit();
    };
    if (options.onLookThrough) {
      this.root.classList.add('mt-colour-view--look');
      sunHead.append(makeLook());
    }

    // Mobile footer: Verres — the reset; Sunset — Clair / Foncé, « Voir le paysage », the reset.
    const foot = h('div', 'mt-cfoot');
    const chipsRow = h('div', 'mt-int-chips mt-only-sun'); chipsRow.setAttribute('role', 'group'); chipsRow.setAttribute('aria-label', w.sections[3]);
    makeIntensity('mt-int-chip', chipsRow);
    foot.append(h('span', 'mt-note mt-only-classic', ''), chipsRow);
    if (options.onLookThrough) { const look = makeLook(); look.classList.add('mt-only-sun'); foot.append(look); }
    foot.append(makeReset());
    const deskFoot = h('div', 'mt-cfoot-desk');
    deskFoot.append(h('span', 'mt-note', ''), makeReset());
    this.root.append(tabs, lensSec, sunSec, intSec, foot, deskFoot);
    setTab(isSunset() ? 'sun' : 'lens');
    refreshers.forEach(r => r());
  }

  /** Current live choice (copy). */
  current(): AppearanceChoice { return { ...this.choice }; }

  /** A110T: lens and intensity chosen in « Voir le paysage », shown on the frame when the try-on resumes. */
  selectSunset(lens: string, density: number): void { this.applyLens(lens, density); }
}
